import { useMemo, useState } from 'react';
import { Check, RefreshCw, Smartphone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getDevicePlatform, platformHealthName } from '@/lib/device';

type SyncStage = 'idle' | 'connecting' | 'reading' | 'done';

interface HealthSyncCardProps {
  /** Called with simulated vitals once the staged sync completes. */
  onVitals: (heartRate: number, hrv: number) => void;
}

/**
 * Device-aware health sync card. Detects iPhone (Apple Health) vs Android
 * (Health Connect) vs desktop, then runs a staged, clearly-labelled
 * *simulated* sync — browsers can't reach HealthKit/Health Connect directly,
 * so a real pipeline requires a native companion app or OAuth backend.
 */
export function HealthSyncCard({ onVitals }: HealthSyncCardProps) {
  const platform = useMemo(() => getDevicePlatform(), []);
  const healthName = platformHealthName(platform);
  const [stage, setStage] = useState<SyncStage>('idle');

  const deviceLabel =
    platform === 'ios' ? 'iPhone' : platform === 'android' ? 'Android' : 'Desktop / Web';

  const connect = async () => {
    setStage('connecting');
    await new Promise((resolve) => setTimeout(resolve, 700));
    setStage('reading');
    await new Promise((resolve) => setTimeout(resolve, 900));

    // Simulated plausible resting vitals.
    const heartRate = 58 + Math.round(Math.random() * 14);
    const hrv = 42 + Math.round(Math.random() * 26);
    onVitals(heartRate, hrv);
    setStage('done');
  };

  const busy = stage === 'connecting' || stage === 'reading';

  return (
    <div className="space-y-3 rounded-2xl border border-aqua-strong/30 bg-gradient-to-br from-aqua/70 to-white p-4">
      <div className="flex items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-aqua-strong text-white">
          <Smartphone className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-aqua-foreground">
            {deviceLabel} detected · {healthName}
          </p>
          <p className="text-xs text-muted-foreground">
            {stage === 'done'
              ? 'Last synced just now · simulated'
              : busy
                ? stage === 'connecting'
                  ? `Authorizing with ${healthName}…`
                  : 'Reading today’s vitals…'
                : `Sync resting HR & HRV from ${healthName}`}
          </p>
        </div>
        <Button
          type="button"
          onClick={connect}
          disabled={busy}
          className={cn(
            'h-11 shrink-0 rounded-xl px-4 font-bold',
            stage === 'done'
              ? 'bg-emerald-600 text-white hover:bg-emerald-600/90'
              : 'bg-aqua-strong text-white hover:bg-aqua-strong/90',
          )}
        >
          {busy ? (
            <>
              <RefreshCw className="size-4 animate-spin" aria-hidden />
              Syncing…
            </>
          ) : stage === 'done' ? (
            <>
              <Check className="size-4" aria-hidden />
              Synced
            </>
          ) : (
            'Connect & sync'
          )}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Simulated in this web version — browsers can&apos;t access {healthName} directly. For a
        real measurement, use the camera pulse scan below.
      </p>
    </div>
  );
}
